import { useDrag } from '@use-gesture/react'
import defer from 'lodash/defer'
import isNumber from 'lodash/isNumber'
import { Dispatch, RefObject, SetStateAction, useRef, useState } from 'react'

const DRAG_SCREEN_THRESHOLD = 0.4

interface HorizontalProps {
  activeSlide: number
  itemSize: number
  itemWidth
  max?: number
  min?: number
  moveToExact?: number
  mx?: number
  setActiveSlide: Dispatch<SetStateAction<number>>
  startPosition?: number
  target: RefObject<any>
  threshold?: number
}

export const CalculateHorizontal = ({
  activeSlide,
  itemSize,
  itemWidth,
  max,
  min = 0,
  moveToExact,
  mx,
  setActiveSlide,
  target,
  startPosition = 0,
  threshold = DRAG_SCREEN_THRESHOLD,
}: HorizontalProps) => {
  if (!max) {
    // eslint-disable-next-line no-param-reassign
    max = itemSize - Math.floor(target.current.clientWidth / itemWidth)
  }
  const updateTranslate = (currentSlide) => {
    const newTranslate = -currentSlide * itemWidth
    if (target.current) {
      // eslint-disable-next-line no-param-reassign
      target.current.style.transform = `translateX(${startPosition + newTranslate}px)`
    }
    setActiveSlide(currentSlide)
  }

  const isAboveMax = newX => newX >= Math.abs(max!)
  const isBelowMin = newX => newX < Math.abs(min)
  if (isNumber(moveToExact)) {
    const newX = moveToExact
    if (isBelowMin(newX)) {
      updateTranslate(min)
    } else if (isAboveMax(newX)) {
      updateTranslate(max)
    } else {
      updateTranslate(moveToExact)
    }
  } else if (mx) {
    const isSlidingRight = mx < -threshold
    const isSlidingLeft = mx > threshold
    if (isSlidingLeft) {
      const newX = activeSlide - Math.ceil(Math.abs(mx) / itemWidth)
      updateTranslate(isBelowMin(newX) ? min : newX)
    } else if (isSlidingRight) {
      const newX = Math.abs(Math.ceil(Math.abs(mx) / itemWidth) + activeSlide)
      updateTranslate(isAboveMax(newX) ? max : newX)
    }
  }
}

interface CustomDragProps<T> {
  disableDrag?: boolean
  dragBounds?: boolean
  itemSize: number
  itemWidth: number
  startPosition?: number
  target: RefObject<T>
}

export function useHorizontalSlider<T extends HTMLElement>(
  config: CustomDragProps<T>,
) {
  const {
    disableDrag,
    dragBounds = true,
    itemSize,
    itemWidth,
    startPosition = 0,
    target,
  } = config

  const [activeSlide, setActiveSlide] = useState(0)
  const isDragging = useRef<boolean>(false)

  const setSlide = (value) => {
    if (target.current) {
      CalculateHorizontal({
        target,
        activeSlide,
        setActiveSlide,
        itemWidth,
        moveToExact: value,
        itemSize,
        startPosition,
      })
    }
  }

  useDrag(
    ({
      first,
      last,
      dragging,
      movement: [mx],
      xy: [x, y],
      memo = {},
      tap,
      cancel,
    }) => {
      if (disableDrag) {
        return null
      }
      if (first) {
        isDragging.current = !!dragging
      }

      if (!memo.bounds && target.current) {
        // eslint-disable-next-line no-param-reassign
        memo = {
          ...memo,
          mx,
          bounds: target?.current?.parentElement?.getBoundingClientRect(),
          threshold: itemWidth * DRAG_SCREEN_THRESHOLD,
          max: itemSize - Math.floor(target.current.clientWidth / itemWidth),
          min: 0,
        }
      }
      if (tap) {
        return memo
      }
      const next = {
        ...memo,
        mx,
      }

      if (!target?.current) {
        cancel()
        return memo
      }

      if (!next.bounds) {
        return next
      }

      if (dragBounds && (x < memo.bounds.left || x > memo.bounds.right || y < memo.bounds.top || y > memo.bounds.bottom)) {
        cancel()
      }

      if (!last) {
        const newTranslate = -activeSlide * itemWidth + mx
        // eslint-disable-next-line no-param-reassign
        target.current.style.transform = `translateX(${startPosition + newTranslate}px)`
        return next
      }

      if (last) {
        defer(() => {
          isDragging.current = !!dragging
        })
        CalculateHorizontal({ ...config, ...memo, setActiveSlide, activeSlide })
      }
      return next
    },
    {
      axis: 'x',
      swipe: { velocity: 10000000000000000 },
      axisThreshold: { pen: 16, mouse: 10, touch: 10 },
      target,
      preventScrollAxis: 'y',
      filterTaps: false,
    },
  )

  return {
    goToNextSlide: () => setSlide(activeSlide + 1),
    goToPreviousSlide: () => setSlide(activeSlide - 1),
    activeSlide,
    isDragging,
  }
}
